(() => {
  const MOBILE_QUERY = "(max-width: 749px)";

  /**
   * Force the device-appropriate source on a background video.
   *
   * The `media` attribute on a <video>'s <source> is unreliable (it is really a
   * <picture> feature) and is evaluated only once at load. Back/forward
   * navigation restores the page from the bfcache without re-running source
   * selection, which can leave the wrong-device file showing (e.g. the desktop
   * video on mobile). Setting the `src` attribute directly takes precedence over
   * the <source> children, so we pick the right file deterministically here.
   *
   * @param {HTMLVideoElement} video
   */
  const applyDeviceSource = (video) => {
    let desktopSrc = "";
    let mobileSrc = "";

    video.querySelectorAll("source").forEach((source) => {
      const url = source.getAttribute("src");
      if (!url) {
        return;
      }
      if (source.getAttribute("media")) {
        mobileSrc = url;
      } else {
        desktopSrc = url;
      }
    });

    const isMobile = window.matchMedia(MOBILE_QUERY).matches;
    const wanted = isMobile ? mobileSrc || desktopSrc : desktopSrc || mobileSrc;

    if (!wanted) {
      return;
    }

    // Only swap when the browser does not already have the right file loaded,
    // so we avoid a needless reload/flash when selection was already correct.
    const wantedAbsolute = new URL(wanted, window.location.href).href;
    if (video.currentSrc !== wantedAbsolute) {
      video.setAttribute("src", wanted);
      video.load();
    }
  };

  const applyBackgroundVideos = () => {
    /** @type {NodeListOf<HTMLVideoElement>} */
    const videoElements = document.querySelectorAll(
      "video[data-password-period-video]"
    );

    if (!videoElements.length) {
      return;
    }

    const hour = new Date().getHours();
    let period = "night";

    if (hour >= 6 && hour < 12) {
      period = "morning";
    } else if (hour >= 12 && hour < 18) {
      period = "day";
    } else if (hour >= 18 && hour < 24) {
      period = "evening";
    }

    const selectedVideo = Array.from(videoElements).find(
      (videoElement) => videoElement.dataset.period === period
    );
    const activeVideo = selectedVideo || videoElements[0];

    videoElements.forEach((videoElement) => {
      applyDeviceSource(videoElement);

      const shouldShow = videoElement === activeVideo;

      videoElement.classList.toggle("is-active", shouldShow);
      videoElement.muted = true;
      videoElement.playsInline = true;

      if (!shouldShow) {
        videoElement.pause();
        return;
      }

      const playPromise = videoElement.play();

      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Browsers can block autoplay in edge cases; keep silent fallback.
        });
      }
    });
  };

  applyBackgroundVideos();

  // Re-evaluate when the page is restored from the back/forward cache, which
  // otherwise keeps whatever source/state was active when the user left.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      applyBackgroundVideos();
    }
  });
})();
